import {
  ApiMortgageTypes,
  InterestProvidersListFiltersFormFields,
  InterestProvidersListMortgageTypes,
  InterestProvidersListMortgageTypesEn
} from '../../../../@types';
import { assetPrefix, getUrlPaths, utilStringToNumber } from '../../../../utils';
import { typesLang } from '../../../../@types/shared/types';
import { sharedUtilsGetLang } from '../../../../utils/lang-utils';

export const translateMortgageType = (mortgageTypes: ApiMortgageTypes[], locale: typesLang) => {
  if (locale === 'nl') {
    return mortgageTypes;
  }

  return mortgageTypes.map((type) => {
    switch (type.mortgageKey) {
      case 'Annuiteitenhypotheek':
        return { ...type, mortgageName: 'Annuity mortgage' };
      case 'Lineaire_hypotheek':
        return { ...type, mortgageName: 'Linear mortgage' };
      case 'Aflossingsvrije_hypotheek':
        return { ...type, mortgageName: 'Repayment-free mortgage' };
      case 'Beleggershypotheek':
        return { ...type, mortgageName: 'Investment mortgage' };
      case 'Levenhypotheek':
        return { ...type, mortgageName: 'Life mortgage' };
      case 'Spaarhypotheek':
        return { ...type, mortgageName: 'Savings mortgage' };
      default:
        return type;
    }
  });
};

export const translateUrlPartMortgageTypeNlToEn = (
  urlPart: InterestProvidersListMortgageTypes
): InterestProvidersListMortgageTypesEn => {
  switch (urlPart) {
    case 'Annuiteitenhypotheek':
      return 'Annuity_mortgage';
    case 'Lineaire_hypotheek':
      return 'Linear_mortgage';
    case 'Aflossingsvrije_hypotheek':
      return 'Repayment_free_mortgage';
    case 'Beleggershypotheek':
      return 'Investment_mortgage';
    case 'Levenhypotheek':
      return 'Life_mortgage';
    case 'Spaarhypotheek':
      return 'Savings_mortgage';
    default:
      return urlPart;
  }
};

export const translateUrlPartMortgageTypeEnToNl = (
  urlPart: InterestProvidersListMortgageTypesEn
) => {
  switch (urlPart) {
    case 'Annuity_mortgage':
      return 'Annuiteitenhypotheek';
    case 'Linear_mortgage':
      return 'Lineaire_hypotheek';
    case 'Repayment_free_mortgage':
      return 'Aflossingsvrije_hypotheek';
    case 'Investment_mortgage':
      return 'Beleggershypotheek';
    case 'Life_mortgage':
      return 'Levenhypotheek';
    case 'Savings_mortgage':
      return 'Spaarhypotheek';
    default:
      return urlPart;
  }
};

export const convertFiltersToQueryString = (filters: InterestProvidersListFiltersFormFields) => {
  return `HypotheekVorm=${filters.mortgageType}&IsNieuwbouw=${
    filters.buildingType === 'Nieuwbouw'
  }&RenteBasis=${filters.interestTariff}&RentevastePeriode=${filters.mortgageTerm}&EnergyLabel=${
    filters.energyLabel
  }`;
};

const paramToUrlPart = (
  param: string,
  type: keyof InterestProvidersListFiltersFormFields,
  locale?: typesLang
) => {
  let tmp = '';
  locale = !locale ? sharedUtilsGetLang() : locale;
  const variableLabel = locale === 'en' ? 'variable' : 'variabel';
  const interestLabel = locale === 'en' ? `${param}-year` : `${param}-jaar-rentevast`;

  switch (type) {
    case 'mortgageType':
      if (locale === 'en') {
        tmp = translateUrlPartMortgageTypeNlToEn(param as InterestProvidersListMortgageTypes);
      } else {
        tmp = param;
      }

      tmp = tmp.toLowerCase();
      tmp = tmp.replaceAll('_', '-');
      break;
    case 'mortgageTerm':
      tmp = parseInt(param) === 0 ? variableLabel : interestLabel;
      break;
  }

  return tmp;
};

const urlPartToParam = (
  urlPart: string,
  type: keyof InterestProvidersListFiltersFormFields,
  locale?: typesLang
) => {
  locale = !locale ? sharedUtilsGetLang() : locale;
  let tmp = '';
  let interest: number;
  switch (type) {
    case 'mortgageType':
      tmp = urlPart.charAt(0).toUpperCase() + urlPart.slice(1);
      tmp = tmp.replaceAll('-', '_');
      if (locale === 'en') {
        tmp = translateUrlPartMortgageTypeEnToNl(tmp);
      }
      break;
    case 'mortgageTerm':
      interest = parseInt(urlPart.split('-')[0]);
      tmp = interest > 0 ? interest.toString() : '0';
      // if 0 then variabel
      // else digit-jaar-rentevast
      break;
  }

  return tmp;
};

export const rewritePath = (params: InterestProvidersListFiltersFormFields, locale: typesLang) => {
  const urlParts = getUrlPaths();
  let url = `/${urlParts[0]}`;
  if (locale === 'en') {
    url += `/${urlParts[1]}`;
  }
  url += `/${paramToUrlPart(params.mortgageType, 'mortgageType')}/${paramToUrlPart(
    params.mortgageTerm,
    'mortgageTerm'
  )}/`;
  window.history.pushState(
    { urlPath: url },
    `Hypotheekrente ${params.mortgageType} ${params.mortgageTerm} | De Hypotheker `,
    url
  );
};

export const rewriteLanguageSwitch = (
  params: InterestProvidersListFiltersFormFields,
  locale: typesLang
) => {
  const revertedLocale = locale === 'nl' ? 'en' : 'nl';
  const switchUrl = `${
    locale === 'nl' ? '/en/mortgage-interest-rates/' : '/rentestanden/'
  }/${paramToUrlPart(params.mortgageType, 'mortgageType', revertedLocale)}/${paramToUrlPart(
    params.mortgageTerm,
    'mortgageTerm',
    revertedLocale
  )}/`;
  const languageLink = document.querySelector(
    '.c-header-corporate__link__language'
  ) as HTMLAnchorElement;

  if (languageLink) {
    languageLink.href = `${window.location.origin}${switchUrl}`;
  }
};
export const rewriteIntroText = (
  params: InterestProvidersListFiltersFormFields,
  locale: typesLang
) => {
  const $h1 = document.getElementsByTagName('h1')[0];
  if (!$h1) {
    return;
  }
  const { mortgageType, mortgageTerm: interestRatePeriod } = params;
  const $mortgageType = document.getElementById('mortgage-type');
  const $interestRatePeriod = document.getElementById('interest-rate-period');

  if ($mortgageType) {
    $mortgageType.innerText = paramToUrlPart(mortgageType, 'mortgageType').replaceAll('-', ' ');
  }

  const interestRatePeriodLabel = locale === 'nl' ? 'jaar rentevast' : 'year';
  const interestRatePeriodVariable = locale === 'nl' ? 'variabel' : 'variable';
  const interestRatePeriodValue =
    utilStringToNumber(interestRatePeriod) > 0 ? interestRatePeriod : 0;

  const innerValue =
    interestRatePeriodValue > 0
      ? `${interestRatePeriodValue} ${interestRatePeriodLabel}`
      : interestRatePeriodVariable;

  if ($interestRatePeriod) {
    $interestRatePeriod.innerText = innerValue;
  }

  $h1.innerHTML = `${
    locale === 'nl' ? 'Hypotheekrente vergelijken - ' : 'Current interest rates - '
  } ${innerValue}`;
};

export const getParamsFromPath = (): InterestProvidersListFiltersFormFields | null => {
  // debug
  //const path = '/rentestanden/beleggershypotheek/10-jaar-rentevast/';
  //const path = '/rentestanden/lineaire-hypotheek/20-jaar-rentevast/
  //const path = '/rentestanden/lineaire-hypotheek/variabel';
  //const path = '/rentestanden';
  let params: InterestProvidersListFiltersFormFields | null = {
    mortgageType: '',
    mortgageTerm: '',
    buildingType: '',
    interestTariff: '',
    energyLabel: ''
  };
  const urlParts = getUrlPaths();
  if (urlParts.length >= 3) {
    params.mortgageType = urlPartToParam(
      urlParts[urlParts.length === 3 ? 1 : 2],
      'mortgageType'
    ) as InterestProvidersListMortgageTypes;
    params.mortgageTerm = urlPartToParam(urlParts[urlParts.length === 3 ? 2 : 3], 'mortgageTerm');
  } else {
    params = null;
  }

  return params;
};
export const getProductImage = (url: string) => {
  if (url == null || url === '') {
    return `https://placehold.it/400x215?text=Geen logo beschikbaar`;
  } else {
    return `${assetPrefix}/assets/interest-providers-list/${url}`;
  }
};

export const isAllowedUrl = (url: string) => {
  const allowedPaths = [
    '/react-tool-interest-providers-list.html',
    '/rentestanden',
    '/rentestanden/',
    '/rentestanden/annuiteitenhypotheek/*',
    '/rentestanden/lineaire-hypotheek/*',
    '/rentestanden/aflossingsvrije-hypotheek/*',
    '/rentestanden/beleggershypotheek/*',
    '/rentestanden/levenhypotheek/*',
    '/rentestanden/spaarhypotheek/*',
    '/en/mortgage-interest-rates',
    '/en/mortgage-interest-rates/',
    '/en/mortgage-interest-rates/annuity-mortgage/*',
    '/en/mortgage-interest-rates/linear-mortgage/*',
    '/en/mortgage-interest-rates/repayment-free-mortgage/*',
    '/en/mortgage-interest-rates/investment-mortgage/*',
    '/en/mortgage-interest-rates/life-mortgage/*',
    '/en/mortgage-interest-rates/savings-mortgage/*'
  ];

  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;

    for (const allowedPath of allowedPaths) {
      if (allowedPath.endsWith('*')) {
        const basePath = allowedPath.slice(0, -1); // Remove the "*"
        if (pathname.startsWith(basePath) && pathname !== basePath) {
          // Check if it starts with the base path AND is not equal to the base path
          return true;
        }
      } else if (pathname === allowedPath) {
        return true;
      }
    }

    return false;
  } catch (error) {
    return false;
  }
};
